import React, { useCallback, useState } from 'react';
import { Customer } from '../../types';
import googleSheets from '../../services/googleSheets';

interface CustomerWithMetrics extends Customer {
  progress: number;
  healthScore?: number;
  daysInOnboarding: number;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

interface CustomerTableProps {
  customers: CustomerWithMetrics[];
  onCustomerClick: (customerId: string) => void;
  onStatusUpdate?: (customerId: string, status: Customer['onboardingStatus']) => void;
  onEditCustomer?: (customer: CustomerWithMetrics) => void;
}

const CustomerTable: React.FC<CustomerTableProps> = React.memo(({ customers, onCustomerClick, onStatusUpdate, onEditCustomer }) => {
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const handleStatusChange = useCallback(async (customerId: string, newStatus: Customer['onboardingStatus'], selectElement: HTMLSelectElement) => {
    if (!onStatusUpdate) return;
    
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    
    // Validate status change against actual progress
    const customerProgress = customer.progress || 0;
    let isValidStatusChange = true;
    let warningMessage = '';
    
    if (newStatus === 'completed' && customerProgress < 100) {
      isValidStatusChange = false;
      warningMessage = `Cannot set status to "Completed" when progress is only ${Math.round(customerProgress)}%. Customer must complete all required tasks first.`;
    } else if (newStatus === 'not_started' && customerProgress > 0) {
      isValidStatusChange = false;
      warningMessage = `Cannot set status to "Not Started" when progress is ${Math.round(customerProgress)}%. Customer has already completed some tasks.`;
    }
    
    // Show warning and prevent invalid status changes
    if (!isValidStatusChange) {
      alert(warningMessage);
      // Revert dropdown to original value
      const originalStatus = customer.onboardingStatus || 'not_started';
      selectElement.value = originalStatus;
      return;
    }
    
    // Update dropdown color immediately for better UX
    const newClass = `status-dropdown dropdown-${newStatus.replace('_', '-')}`;
    selectElement.className = newClass;
    
    setUpdatingStatus(customerId);
    try {
      await googleSheets.updateCustomerStatus(customerId, newStatus);
      onStatusUpdate(customerId, newStatus);
      
      // Show confirmation for manual status overrides
      if ((newStatus === 'in_progress' && customerProgress === 0) ||
          (newStatus === 'in_progress' && customerProgress === 100)) {
        console.warn(`Manual status override: ${customer.companyName} set to "${newStatus}" with ${customerProgress}% progress`);
      }
    } catch (error) {
      console.error('Status update failed:', error);
      // Revert the color on error
      const originalStatus = customer.onboardingStatus || 'not_started';
      const originalClass = `status-dropdown dropdown-${originalStatus.replace('_', '-')}`;
      selectElement.className = originalClass;
      selectElement.value = originalStatus;
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  }, [onStatusUpdate, customers]);

  const getStatusDropdown = useCallback((customer: CustomerWithMetrics) => {
    if (!onStatusUpdate) {
      // Read-only mode - show badge
      const statusClasses = {
        not_started: 'badge-gray',
        in_progress: 'badge-yellow',
        completed: 'badge-green',
      };
      
      const statusLabels = {
        not_started: 'Not Started',
        in_progress: 'In Progress', 
        completed: 'Completed'
      };

      return (
        <span className={`status-badge ${statusClasses[customer.onboardingStatus] || 'badge-gray'}`}>
          {statusLabels[customer.onboardingStatus] || 'Unknown'}
        </span>
      );
    }

    // Editable mode - show dropdown with colored background
    const status = customer.onboardingStatus || 'not_started';
    const dropdownClass = `status-dropdown dropdown-${status.replace('_', '-')}`;
    
    return (
      <select
        value={status}
        onChange={(e) => handleStatusChange(customer.id, e.target.value as Customer['onboardingStatus'], e.target)}
        disabled={updatingStatus === customer.id}
        className={dropdownClass}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Update onboarding status for ${customer.companyName}`}
      >
        <option value="not_started">Not Started</option>
        <option value="in_progress">In Progress</option>
        <option value="completed">Completed</option>
      </select>
    );
  }, [onStatusUpdate, updatingStatus, handleStatusChange]);

  const getHealthScoreClass = useCallback((score?: number) => {
    if (!score) return '';
    if (score >= 80) return 'score-high';
    if (score >= 60) return 'score-medium';
    return 'score-low';
  }, []);

  return (
    <div className="customer-table-container" role="region" aria-label="Customer Data Table">
      <table className="customer-table" role="table" aria-label="Customer onboarding data">
        <thead>
          <tr>
            <th scope="col">Company</th>
            <th scope="col">Segment</th>
            <th scope="col">Status</th>
            <th scope="col">Progress</th>
            <th scope="col">Health Score</th>
            <th scope="col">Days in Onboarding</th>
            <th scope="col">Last Updated By</th>
            {onEditCustomer && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {customers.map((customer, index) => (
            <tr 
              key={customer.id || 'unknown'}
              onClick={() => onCustomerClick(customer.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCustomerClick(customer.id);
                }
              }}
              className="customer-row"
              tabIndex={0}
              role="button"
              aria-label={`View details for ${customer.companyName || 'Unknown Company'}`}
              aria-describedby={`customer-summary-${index}`}
            >
              <td className="company-name" data-label="Company">{customer.companyName || 'Unknown Company'}</td>
              <td className="segment" data-label="Segment">{customer.segment ? customer.segment.replace('_', ' ') : 'Unknown'}</td>
              <td data-label="Status">{getStatusDropdown(customer)}</td>
              <td data-label="Progress">
                <div className="progress-cell">
                  <div 
                    className="mini-progress-bar"
                    role="progressbar"
                    aria-valuenow={customer.progress || 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Onboarding progress: ${Math.round(customer.progress || 0)}%`}
                  >
                    <div 
                      className="mini-progress-fill" 
                      style={{ width: `${customer.progress || 0}%` }}
                    />
                  </div>
                  <span className="progress-text" aria-hidden="true">{Math.round(customer.progress || 0)}%</span>
                </div>
              </td>
              <td data-label="Health Score">
                <span 
                  className={`health-score ${getHealthScoreClass(customer.healthScore)}`}
                  aria-label={`Health score: ${customer.healthScore !== undefined && customer.healthScore !== null ? customer.healthScore : 'Not available'}`}
                >
                  {customer.healthScore !== undefined && customer.healthScore !== null ? customer.healthScore : '-'}
                </span>
              </td>
              <td className="days" data-label="Days in Onboarding">
                <span id={`customer-summary-${index}`} className="sr-only">
                  {customer.companyName}, {customer.progress}% progress, {customer.daysInOnboarding} days in onboarding
                </span>
                {customer.daysInOnboarding || 0} days
              </td>
              <td className="last-updated" data-label="Last Updated By">
                {customer.lastUpdatedBy ? (
                  <div className="updated-info">
                    <div className="updated-by">{customer.lastUpdatedBy}</div>
                    {customer.lastUpdatedDate && (
                      <div className="updated-date">
                        {new Date(customer.lastUpdatedDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="no-activity" title="Update any task to see tracking info">
                    No tracked activity
                  </span>
                )}
              </td>
              {onEditCustomer && (
                <td className="actions" data-label="Actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditCustomer(customer);
                    }}
                    className="edit-btn"
                    title="Edit customer"
                    aria-label={`Edit ${customer.companyName}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {customers.length === 0 && (
        <div className="empty-state">
          <p>No customers found</p>
        </div>
      )}
    </div>
  );
});

CustomerTable.displayName = 'CustomerTable';

export default CustomerTable;